import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography,
  Upload,
  Button,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Table,
  Tag,
  Modal,
  Drawer,
  message,
  Empty,
  Segmented,
  Popconfirm,
  Form,
  Progress,
  Tooltip,
  Alert,
  Tree,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  SearchOutlined,
  FolderAddOutlined,
  ReloadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
  PlusCircleOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const { Title, Text } = Typography;

/** img 標籤不會帶 Authorization，改以 fetch + blob 顯示（並帶上 credentials 以支援 Cookie） */
function KnowledgeAuthImage({ relativePath, token, style, alt = '' }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!relativePath || !token) {
      setBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let created = null;
    const path = String(relativePath).replace(/^\//, '');
    const url = `/Uploads/${path.split('/').map((s) => encodeURIComponent(s)).join('/')}`;

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) {
          let bodyHint = '';
          try {
            const t = await res.clone().text();
            bodyHint = t && t.length > 0 ? ` bodyLen=${t.length} bodySnippet=${t.slice(0, 200).replace(/\s+/g, ' ')}` : '';
          } catch {
            /* ignore */
          }
          // eslint-disable-next-line no-console
          console.warn('[KnowledgeBase preview image]', {
            url,
            status: res.status,
            statusText: res.statusText,
            path,
            ...(bodyHint ? { bodyHint } : {}),
          });
          throw new Error(String(res.status));
        }
        const blob = await res.blob();
        if (!blob || blob.size === 0) {
          // eslint-disable-next-line no-console
          console.warn('[KnowledgeBase preview image] empty blob', { url, path, blobType: blob?.type });
        }
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('[KnowledgeBase preview image] fetch failed', { path, url, error: e?.message || String(e) });
          setBlobUrl(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [relativePath, token]);

  if (!blobUrl) {
    return (
      <div style={{ ...style, background: 'linear-gradient(135deg,#f3e8ff,#efe7ff)', minHeight: style?.minHeight || 48 }} />
    );
  }
  return <img alt={alt} src={blobUrl} style={style} />;
}

export default function KnowledgeBasePage() {
  const { t } = useLanguage();
  const token = useMemo(() => localStorage.getItem('token'), []);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [viewMode, setViewMode] = useState('tile');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(undefined);
  const [uploadCategoryId, setUploadCategoryId] = useState(undefined);
  const [search, setSearch] = useState('');
  const searchRef = useRef(search);
  searchRef.current = search;
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageRef = useRef(page);
  pageRef.current = page;
  const [pageSize] = useState(24);

  const [detailOpen, setDetailOpen] = useState(false);
  const [activeDocId, setActiveDocId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [movingCategory, setMovingCategory] = useState(false);

  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catForm] = Form.useForm();

  // xAI Collection — 分類層級狀態（key: categoryId）
  const [xaiEnabled, setXaiEnabled] = useState(false);
  const [xaiHasMgmtKey, setXaiHasMgmtKey] = useState(false);
  const [xaiCatLoading, setXaiCatLoading] = useState({});  // { [catId]: true/false }

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fakeUploadProgressTimerRef = useRef(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const clearFakeUploadProgress = useCallback(() => {
    if (fakeUploadProgressTimerRef.current != null) {
      clearInterval(fakeUploadProgressTimerRef.current);
      fakeUploadProgressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearFakeUploadProgress(), [clearFakeUploadProgress]);

  const xhrUploadKnowledge = useCallback(
    (formData) =>
      new Promise((resolve, reject) => {
        if (!token) {
          reject(new Error('no token'));
          return;
        }
        clearFakeUploadProgress();
        setUploading(true);
        setUploadProgress(3);
        // 假進度：緩慢爬升到 90%（後端含 LibreOffice 處理時 xhr 仍 pending），完成後再一次拉到 100%
        fakeUploadProgressTimerRef.current = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 90) return prev;
            const bump = prev < 35 ? 2 + Math.floor(Math.random() * 3) : prev < 65 ? 1.5 + Math.random() * 1.5 : 0.6 + Math.random() * 0.8;
            return Math.min(90, Math.round((prev + bump) * 10) / 10);
          });
        }, 420);

        const xhr = new XMLHttpRequest();
        const url = `${window.location.origin}/api/knowledge-documents/upload`;
        xhr.open('POST', url);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        const finishBar = (then) => {
          clearFakeUploadProgress();
          setUploadProgress(100);
          window.setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            then();
          }, 480);
        };

        xhr.onload = () => {
          let data = {};
          try {
            data = JSON.parse(xhr.responseText || '{}');
          } catch {
            data = {};
          }
          finishBar(() => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.error || xhr.statusText || 'upload failed'));
            }
          });
        };
        xhr.onerror = () => {
          clearFakeUploadProgress();
          setUploading(false);
          setUploadProgress(0);
          reject(new Error('network error'));
        };
        xhr.send(formData);
      }),
    [token, clearFakeUploadProgress]
  );

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch('/api/knowledge-documents/categories', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'load categories failed');
      // 新格式：{ categories, xaiEnabled, xaiHasMgmtKey }；相容舊格式陣列
      if (Array.isArray(data)) {
        setCategories(data);
      } else {
        setCategories(data.categories || []);
        setXaiEnabled(data.xaiEnabled ?? false);
        setXaiHasMgmtKey(data.xaiHasMgmtKey ?? false);
      }
    } catch (e) {
      message.error(e.message);
    }
  }, [authHeaders]);

  const loadList = useCallback(
    async (pageOverride) => {
      const effectivePage = pageOverride != null ? pageOverride : pageRef.current;
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        qs.set('page', String(effectivePage));
        qs.set('pageSize', String(pageSize));
        if (categoryId) qs.set('categoryId', categoryId);
        const st = searchRef.current.trim();
        if (st) qs.set('search', st);
        const res = await fetch(`/api/knowledge-documents?${qs.toString()}`, {
          headers: authHeaders,
          credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'load failed');
        const rows = (data.data || []).map((row) => ({
          ...row,
          id: row.id ?? row.Id,
          title: row.title ?? row.Title,
          previewImagePath: row.previewImagePath ?? row.PreviewImagePath,
          publishedVersionId: row.publishedVersionId ?? row.PublishedVersionId,
          updatedAt: row.updatedAt ?? row.UpdatedAt,
        }));
        setList(rows);
        setTotal(data.total ?? 0);
      } catch (e) {
        message.error(e.message);
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, pageSize, categoryId]
  );

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadList();
  }, [loadList, categoryId]);

  const openDetail = async (id) => {
    setActiveDocId(id);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetch(`/api/knowledge-documents/${id}`, {
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'load detail failed');
      setDetail(data);
    } catch (e) {
      message.error(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      const formData = new FormData();
      formData.append('file', file);
      if (uploadCategoryId) formData.append('categoryId', uploadCategoryId);
      try {
        const data = await xhrUploadKnowledge(formData);
        message.success(t('knowledgeBase.uploadOk'));
        const docId = data.documentId ?? data.document_id;
        setPage(1);
        await loadList(1);
        if (docId) await openDetail(docId);
        onSuccess?.(data);
      } catch (e) {
        message.error(e.message);
        onError?.(e);
      }
    },
  };

  const uploadNewVersionProps = {
    ...uploadProps,
    customRequest: async (options) => {
      const { file, onSuccess, onError } = options;
      if (!activeDocId) return;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentId', activeDocId);
      try {
        const data = await xhrUploadKnowledge(formData);
        message.success(t('knowledgeBase.versionAdded'));
        setPage(1);
        await loadList(1);
        await openDetail(activeDocId);
        onSuccess?.(data);
      } catch (e) {
        message.error(e.message);
        onError?.(e);
      }
    },
  };

  const reindexDocument = async (documentId) => {
    if (!documentId) return;
    try {
      const res = await fetch(`/api/knowledge-documents/${documentId}/reindex`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'reindex failed');
      message.success(t('knowledgeBase.reindexQueued'));
      if (activeDocId === documentId) await openDetail(documentId);
    } catch (e) {
      message.error(e.message);
    }
  };

  const publishVersion = async (versionId) => {
    if (!activeDocId) return;
    try {
      const res = await fetch(`/api/knowledge-documents/${activeDocId}/publish`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ versionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'publish failed');
      message.success(t('knowledgeBase.publishOk'));
      openDetail(activeDocId);
      void loadList();
    } catch (e) {
      message.error(e.message);
    }
  };

  const deleteVersion = async (versionId) => {
    try {
      const res = await fetch(`/api/knowledge-documents/versions/${versionId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'delete failed');
      message.success(t('knowledgeBase.deleteVersionOk'));
      openDetail(activeDocId);
      loadList();
    } catch (e) {
      message.error(e.message);
    }
  };

  const downloadFileAuth = useCallback(
    async (url, fallbackFilename) => {
      const res = await fetch(url, { headers: authHeaders, credentials: 'include' });
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const data = await res.json();
          if (data.error) msg = data.error;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const cd = res.headers.get('Content-Disposition');
      let name = fallbackFilename;
      if (cd) {
        const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
        const quoted = /filename="([^"]+)"/i.exec(cd);
        const plain = /filename=([^;\n]+)/i.exec(cd);
        if (utf8) {
          try {
            name = decodeURIComponent(utf8[1]);
          } catch {
            name = fallbackFilename;
          }
        } else if (quoted) {
          name = quoted[1].trim();
        } else if (plain) {
          name = plain[1].replace(/"/g, '').trim();
        }
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = name || fallbackFilename || 'download';
      a.click();
      URL.revokeObjectURL(objectUrl);
    },
    [authHeaders]
  );

  // xAI Collection — 分類層級建立/移除
  const ensureCategoryCollection = async (catId) => {
    setXaiCatLoading((prev) => ({ ...prev, [catId]: 'ensure' }));
    try {
      const res = await fetch(`/api/knowledge-documents/categories/${catId}/collection`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'ensure failed');
      message.success(t('knowledgeBase.xaiCollectionCreated'));
      await loadCategories();
    } catch (e) {
      message.error(e.message);
    } finally {
      setXaiCatLoading((prev) => ({ ...prev, [catId]: null }));
    }
  };

  const deleteCategoryCollection = async (catId) => {
    setXaiCatLoading((prev) => ({ ...prev, [catId]: 'delete' }));
    try {
      const res = await fetch(`/api/knowledge-documents/categories/${catId}/collection`, {
        method: 'DELETE',
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'delete failed');
      message.success(t('knowledgeBase.xaiCollectionRemoved'));
      await loadCategories();
    } catch (e) {
      message.error(e.message);
    } finally {
      setXaiCatLoading((prev) => ({ ...prev, [catId]: null }));
    }
  };

  const downloadLatestVersion = useCallback(async () => {
    const vs = detail?.versions || [];
    if (!vs.length) {
      message.warning(t('knowledgeBase.downloadNoVersions'));
      return;
    }
    const best = vs.reduce((a, b) => {
      const an = a.versionNo ?? a.VersionNo ?? 0;
      const bn = b.versionNo ?? b.VersionNo ?? 0;
      return an >= bn ? a : b;
    });
    const vid = best.id ?? best.Id;
    const fn = best.originalFileName ?? best.OriginalFileName ?? 'file';
    try {
      await downloadFileAuth(`/api/knowledge-documents/versions/${vid}/download`, fn);
    } catch (e) {
      message.error(e.message);
    }
  }, [detail?.versions, downloadFileAuth, t]);

  const downloadAllVersionsZip = useCallback(async () => {
    if (!activeDocId) return;
    const title = detail?.document?.title || 'document';
    setDownloadingZip(true);
    try {
      await downloadFileAuth(
        `/api/knowledge-documents/${activeDocId}/download-all-versions-zip`,
        `${title}_all_versions.zip`
      );
    } catch (e) {
      message.error(e.message);
    } finally {
      setDownloadingZip(false);
    }
  }, [activeDocId, detail?.document?.title, downloadFileAuth]);

  const deleteDocument = async (documentId) => {
    try {
      const res = await fetch(`/api/knowledge-documents/${documentId}`, {
        method: 'DELETE',
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'delete failed');
      message.success(t('knowledgeBase.deleteDocumentOk'));
      if (activeDocId === documentId) {
        setDetailOpen(false);
        setActiveDocId(null);
        setDetail(null);
      }
      await loadList(pageRef.current);
    } catch (e) {
      message.error(e.message);
    }
  };

  const moveDocumentCategory = async (documentId, newCategoryId) => {
    setMovingCategory(true);
    try {
      const res = await fetch(`/api/knowledge-documents/${documentId}/category`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ categoryId: newCategoryId ?? null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'move failed');
      message.success(t('knowledgeBase.moveCategoryOk') || '分類已更新');
      // 刷新文件詳情
      if (activeDocId === documentId) {
        await openDetail(documentId);
      }
      await loadList(pageRef.current);
    } catch (e) {
      message.error(e.message);
    } finally {
      setMovingCategory(false);
    }
  };

  const deleteCategory = async (catId) => {
    try {
      const res = await fetch(`/api/knowledge-documents/categories/${catId}`, {
        method: 'DELETE',
        headers: authHeaders,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.code === 'xai_collection_linked') {
          message.error(t('knowledgeBase.catDeleteBlockedXai') || '請先移除 xAI Collection 再刪除分類');
        } else if (data.code === 'has_documents') {
          message.error(t('knowledgeBase.catDeleteBlockedDocs') || '分類下仍有文件，請先移動或刪除');
        } else if (data.code === 'has_children') {
          message.error(t('knowledgeBase.catDeleteBlockedChildren') || '請先刪除子分類');
        } else {
          message.error(data.error || 'delete failed');
        }
        return;
      }
      message.success(t('knowledgeBase.categoryDeleted') || '分類已刪除');
      if (categoryId === catId) setCategoryId(undefined);
      loadCategories();
    } catch (e) {
      message.error(e.message);
    }
  };

  const reorderCategories = useCallback(async (orderedIds) => {
    try {
      const items = orderedIds.map((id, idx) => ({ id, sortOrder: idx }));
      await fetch('/api/knowledge-documents/categories/reorder', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(items),
      });
      // 樂觀更新：不需要 reload，本地 categories 順序已由 Tree onDrop 更新
    } catch (e) {
      message.error(e.message);
    }
  }, [authHeaders]);

  const createCategory = async () => {
    try {
      const v = await catForm.validateFields();
      const res = await fetch('/api/knowledge-documents/categories', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: v.name, parentId: v.parentId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'create failed');
      message.success(t('knowledgeBase.categoryCreated'));
      setCatModalOpen(false);
      catForm.resetFields();
      loadCategories();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message);
    }
  };

  const categoryOptions = useMemo(
    () => [
      { value: null, label: t('knowledgeBase.allCategories') },
      ...(categories || []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [categories, t]
  );

  const versionColumns = [
    {
      title: t('knowledgeBase.colVersion'),
      dataIndex: 'versionNo',
      width: 64,
      fixed: 'left',
    },
    {
      title: t('knowledgeBase.colStatus'),
      dataIndex: 'status',
      width: 96,
      render: (s, row) => {
        const err = row.errorMessage ?? row.ErrorMessage;
        const tag =
          s === 'ready' ? (
            <Tag color="green">{s}</Tag>
          ) : s === 'failed' ? (
            <Tag color="red">{s}</Tag>
          ) : (
            <Tag>{s}</Tag>
          );
        if (s === 'failed' && err) {
          return (
            <Tooltip title={String(err)}>
              <span>{tag}</span>
            </Tooltip>
          );
        }
        return tag;
      },
    },
    // 本地向量索引欄位：xAI Collections 模式下隱藏
    ...(!xaiEnabled ? [{
      title: t('knowledgeBase.colIndexStatus'),
      key: 'indexStatus',
      width: 108,
      render: (_, row) => {
        const idx = row.indexStatus ?? row.IndexStatus;
        if (!idx) return <Tag>{t('knowledgeBase.indexNone')}</Tag>;
        if (idx === 'ready') return <Tag color="blue">{idx}</Tag>;
        if (idx === 'failed') return <Tag color="red">{idx}</Tag>;
        if (idx === 'processing' || idx === 'pending') return <Tag color="orange">{idx}</Tag>;
        return <Tag>{idx}</Tag>;
      },
    },
    {
      title: t('knowledgeBase.colChunks'),
      key: 'chunkCount',
      width: 72,
      render: (_, row) => row.chunkCount ?? row.ChunkCount ?? '—',
    }] : []),
    // xAI 同步欄位：xAI Collections 模式下顯示
    ...(xaiEnabled ? [{
      title: t('knowledgeBase.colXaiSync'),
      key: 'xaiSync',
      width: 108,
      render: (_, row) => {
        const fid = row.xaiFileId ?? row.XaiFileId;
        if (fid) return <Tag color="blue">{t('knowledgeBase.xaiSynced')}</Tag>;
        return (
          <Tooltip title={t('knowledgeBase.xaiNotSyncedHint')}>
            <Tag color="default">{t('knowledgeBase.xaiNotSynced')}</Tag>
          </Tooltip>
        );
      },
    }] : []),
    {
      title: t('knowledgeBase.colCreated'),
      dataIndex: 'createdAt',
      width: 148,
      render: (x) => (x ? dayjs(x).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: t('knowledgeBase.colActions'),
      key: 'actions',
      width: 248,
      fixed: 'right',
      render: (_, row) => {
        const vid = row.id ?? row.Id;
        const fn = row.originalFileName ?? row.OriginalFileName ?? `v${row.versionNo ?? row.VersionNo}`;
        return (
          <Space size={4} wrap={false}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={async () => {
                try {
                  await downloadFileAuth(`/api/knowledge-documents/versions/${vid}/download`, fn);
                } catch (e) {
                  message.error(e.message);
                }
              }}
            >
              {t('knowledgeBase.download')}
            </Button>
            <Button
              size="small"
              type="primary"
              disabled={row.status !== 'ready'}
              onClick={() => publishVersion(vid)}
            >
              {t('knowledgeBase.publish')}
            </Button>
            <Popconfirm title={t('knowledgeBase.confirmDeleteVersion')} onConfirm={() => deleteVersion(vid)}>
              <Button size="small" danger icon={<DeleteOutlined />}>
                {t('knowledgeBase.delete')}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // 構建左側 category tree 的 treeData
  const categoryTreeData = useMemo(() => {
    const roots = (categories || []).filter((c) => !c.parentId);
    const children = (categories || []).filter((c) => c.parentId);
    const buildNode = (cat) => {
      const kids = children.filter((c) => c.parentId === cat.id).map(buildNode);
      const hasCollection = !!cat.xaiCollectionId;
      return {
        key: cat.id,
        title: (
          <Space size={4} align="center" style={{ width: '100%', justifyContent: 'space-between', paddingRight: 4 }}>
            <Space size={4} align="center">
              <span>{cat.name}</span>
              {xaiEnabled && (
                hasCollection
                  ? <Tooltip title={cat.xaiCollectionId}><CheckCircleOutlined style={{ color: '#1890ff', fontSize: 11 }} /></Tooltip>
                  : <Tooltip title={t('knowledgeBase.xaiCollectionNotCreated')}><PlusCircleOutlined style={{ color: '#bbb', fontSize: 11 }} /></Tooltip>
              )}
            </Space>
            <Space size={0} onClick={(e) => e.stopPropagation()}>
              {xaiEnabled && xaiHasMgmtKey && (
                hasCollection ? (
                  <Popconfirm
                    title={t('knowledgeBase.xaiCollectionConfirmRemove')}
                    onConfirm={() => deleteCategoryCollection(cat.id)}
                    okText={t('knowledgeBase.xaiCollectionRemove')}
                    okType="danger"
                    cancelText={t('common.cancel') || '取消'}
                  >
                    <Button type="link" danger size="small" loading={xaiCatLoading[cat.id] === 'delete'} style={{ padding: '0 2px', fontSize: 11 }}>
                      {t('knowledgeBase.xaiCollectionRemove')}
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    type="link"
                    size="small"
                    loading={xaiCatLoading[cat.id] === 'ensure'}
                    onClick={() => ensureCategoryCollection(cat.id)}
                    style={{ padding: '0 2px', fontSize: 11 }}
                  >
                    {t('knowledgeBase.xaiCollectionCreate')}
                  </Button>
                )
              )}
              {/* 刪除本機分類：有 xAI Collection 時顯示禁用提示，否則正常刪除 */}
              <Tooltip title={hasCollection ? (t('knowledgeBase.catDeleteBlockedXai') || '請先移除 xAI Collection') : (t('knowledgeBase.deleteCategory') || '刪除分類')}>
                {hasCollection ? (
                  <Button type="link" size="small" danger disabled style={{ padding: '0 2px', fontSize: 11 }}>
                    <DeleteOutlined />
                  </Button>
                ) : (
                  <Popconfirm
                    title={t('knowledgeBase.confirmDeleteCategory') || '確定刪除此分類？'}
                    onConfirm={() => deleteCategory(cat.id)}
                    okText={t('common.ok')}
                    okType="danger"
                    cancelText={t('common.cancel') || '取消'}
                  >
                    <Button type="link" size="small" danger style={{ padding: '0 2px', fontSize: 11 }}>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                )}
              </Tooltip>
            </Space>
          </Space>
        ),
        icon: categoryId === cat.id ? <FolderOpenOutlined /> : <FolderOutlined />,
        children: kids.length ? kids : undefined,
      };
    };
    return roots.map(buildNode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, categoryId, xaiEnabled, xaiHasMgmtKey, xaiCatLoading, t]);

  return (
    <div style={{ padding: '16px 24px' }}>
      <style>{`
        .kb-published-row td { background-color: #f9f0ff !important; }
        .kb-published-row:hover td { background-color: #f0e6ff !important; }
      `}</style>
      {/* 頂部標題列 */}
      <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Space wrap align="center">
            <Input
              allowClear
              style={{ width: 260 }}
              placeholder={t('knowledgeBase.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => { setPage(1); void loadList(1); }}
            />
            <Button icon={<SearchOutlined />} onClick={() => { setPage(1); void loadList(1); }}>
              {t('knowledgeBase.search')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadList}>{t('common.refresh') || 'Refresh'}</Button>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { label: t('knowledgeBase.viewTile'), value: 'tile' },
                { label: t('knowledgeBase.viewList'), value: 'list' },
              ]}
            />
          </Space>
          <div style={{ textAlign: 'right' }}>
            <Title level={4} style={{ margin: 0 }}>{t('knowledgeBase.title')}</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('knowledgeBase.subtitle')}</Text>
          </div>
        </Space>
      </Card>

      {/* 兩欄主體 */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* 左欄：分類 Tree */}
        <Card
          style={{ width: 220, flexShrink: 0, minHeight: 400 }}
          bodyStyle={{ padding: '8px 4px' }}
        >
          {/* 未分類 */}
          <div
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              borderRadius: 4,
              background: categoryId === undefined || categoryId === null ? '#f0f4ff' : 'transparent',
              fontWeight: categoryId === undefined || categoryId === null ? 600 : 400,
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            onClick={() => { setCategoryId(undefined); setPage(1); }}
          >
            <InboxOutlined style={{ color: '#aaa' }} />
            <span>{t('knowledgeBase.allCategories')}</span>
          </div>
          <Divider style={{ margin: '4px 0 6px' }} />
          {categories.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12, padding: '0 12px' }}>
              {t('knowledgeBase.noCategoryYet')}
            </Text>
          ) : (
            <Tree
              showIcon
              blockNode
              draggable={{ icon: false }}
              defaultExpandAll
              selectedKeys={categoryId ? [categoryId] : []}
              onSelect={(keys) => {
                const key = keys[0] ?? undefined;
                setCategoryId(key);
                setPage(1);
              }}
              onDrop={({ node, dragNode, dropPosition, dropToGap }) => {
                // 只支援同層拖排（dropToGap），不支援拖入子節點
                if (!dropToGap) return;
                setCategories((prev) => {
                  const dragId = dragNode.key;
                  const targetId = node.key;
                  const list = [...prev];
                  const fromIdx = list.findIndex((c) => c.id === dragId);
                  const toIdx = list.findIndex((c) => c.id === targetId);
                  if (fromIdx === -1 || toIdx === -1) return prev;
                  const [moved] = list.splice(fromIdx, 1);
                  const insertAt = dropPosition < 0 ? 0 : toIdx + (fromIdx < toIdx ? 0 : 1);
                  list.splice(insertAt, 0, moved);
                  // 非同步儲存，不阻塞 UI
                  void reorderCategories(list.map((c) => c.id));
                  return list;
                });
              }}
              treeData={categoryTreeData}
              style={{ fontSize: 13 }}
            />
          )}
          <Divider style={{ margin: '8px 0 6px' }} />
          <div style={{ padding: '0 8px' }}>
            <Button
              block
              size="small"
              icon={<FolderAddOutlined />}
              onClick={() => setCatModalOpen(true)}
            >
              {t('knowledgeBase.newCategory')}
            </Button>
          </div>
        </Card>

        {/* 右欄：文件列表 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card bodyStyle={{ padding: '12px 16px' }}>
          {uploading ? (
            <div style={{ marginBottom: 12 }}>
              <Progress percent={uploadProgress} status="active" />
            </div>
          ) : null}
          <Space style={{ marginBottom: 16 }} wrap>
            <Select
              allowClear
              placeholder={t('knowledgeBase.uploadSelectCategory')}
              style={{ minWidth: 160 }}
              value={uploadCategoryId ?? categoryId}
              onChange={setUploadCategoryId}
              options={(categories || []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <Upload {...uploadProps}>
              <Button type="primary" icon={<UploadOutlined />}>
                {t('knowledgeBase.uploadNew')}
              </Button>
            </Upload>
          </Space>

          {loading && viewMode === 'tile' ? (
            <div style={{ padding: 48, textAlign: 'center' }}>{t('common.loading')}</div>
          ) : !loading && list.length === 0 ? (
            <Empty description={t('knowledgeBase.empty')} />
          ) : viewMode === 'tile' ? (
            <Row gutter={[16, 16]}>
              {list.map((item) => (
                <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                  <Card
                    hoverable
                    onClick={() => openDetail(item.id)}
                    extra={
                      <div
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Popconfirm
                          title={t('knowledgeBase.confirmDeleteDocument')}
                          onConfirm={() => deleteDocument(item.id)}
                        >
                          <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                            {t('knowledgeBase.deleteDocument')}
                          </Button>
                        </Popconfirm>
                      </div>
                    }
                    cover={
                      <div
                        style={{
                          height: 160,
                          background: '#f5f0ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {item.previewImagePath ? (
                          <KnowledgeAuthImage
                            relativePath={item.previewImagePath}
                            token={token}
                            style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }}
                          />
                        ) : (
                          <Text type="secondary">{t('knowledgeBase.noPreview')}</Text>
                        )}
                      </div>
                    }
                  >
                    <Card.Meta
                      title={item.title}
                      description={
                        <Text type="secondary" ellipsis>
                          {item.publishedVersionId ? (
                            <Tag color="purple">{t('knowledgeBase.published')}</Tag>
                          ) : (
                            <Tag>{t('knowledgeBase.draft')}</Tag>
                          )}
                        </Text>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Table
              rowKey="id"
              loading={loading}
              dataSource={list}
              pagination={{
                current: page,
                pageSize,
                total,
                onChange: (p) => {
                  setPage(p);
                  void loadList(p);
                },
                showSizeChanger: false,
              }}
              onRow={(record) => ({
                onClick: () => openDetail(record.id),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: '',
                  dataIndex: 'previewImagePath',
                  width: 72,
                  render: (p) =>
                    p ? (
                      <KnowledgeAuthImage
                        relativePath={p}
                        token={token}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    ),
                },
                { title: t('knowledgeBase.colTitle'), dataIndex: 'title', ellipsis: true },
                {
                  title: t('knowledgeBase.colPublished'),
                  dataIndex: 'publishedVersionId',
                  width: 100,
                  render: (v) => (v ? <Tag color="purple">{t('knowledgeBase.yes')}</Tag> : <Tag>{t('knowledgeBase.no')}</Tag>),
                },
                {
                  title: t('knowledgeBase.colUpdated'),
                  dataIndex: 'updatedAt',
                  width: 180,
                  render: (x) => (x ? dayjs(x).format('YYYY-MM-DD HH:mm') : '—'),
                },
                {
                  title: t('knowledgeBase.colDocumentActions'),
                  key: 'docActions',
                  width: 120,
                  render: (_, record) => (
                    <Popconfirm
                      title={t('knowledgeBase.confirmDeleteDocument')}
                      onConfirm={() => deleteDocument(record.id)}
                    >
                      <Button
                        type="link"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('knowledgeBase.deleteDocument')}
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]}
            />
          )}

          {viewMode === 'tile' && total > pageSize && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                disabled={page <= 1}
                onClick={() => {
                  const p = Math.max(1, page - 1);
                  setPage(p);
                  void loadList(p);
                }}
              >
                {t('knowledgeBase.prev')}
              </Button>
              <span style={{ margin: '0 16px' }}>
                {page} / {Math.max(1, Math.ceil(total / pageSize))}
              </span>
              <Button
                disabled={page >= Math.ceil(total / pageSize)}
                onClick={() => {
                  const p = page + 1;
                  setPage(p);
                  void loadList(p);
                }}
              >
                {t('knowledgeBase.next')}
              </Button>
            </div>
          )}
          </Card>
        </div>
      </div>

      <Drawer
        width={1000}
        styles={{ body: { paddingBottom: 24 } }}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={detail?.document?.title || t('knowledgeBase.detailTitle')}
      >
        {detailLoading ? (
          <Text type="secondary">{t('common.loading')}</Text>
        ) : detail ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {uploading ? (
              <div style={{ width: '100%' }}>
                <Progress percent={uploadProgress} status="active" />
              </div>
            ) : null}
            <Space wrap>
              <Upload {...uploadNewVersionProps}>
                <Button type="primary" icon={<UploadOutlined />}>
                  {t('knowledgeBase.addVersion')}
                </Button>
              </Upload>
              {/* 本地向量索引：xAI Collections 模式下隱藏 */}
              {!xaiEnabled && (
                <Button icon={<ReloadOutlined />} onClick={() => activeDocId && reindexDocument(activeDocId)}>
                  {t('knowledgeBase.reindex')}
                </Button>
              )}
              <Button icon={<DownloadOutlined />} onClick={() => void downloadLatestVersion()}>
                {t('knowledgeBase.downloadLatest')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={downloadingZip}
                onClick={() => void downloadAllVersionsZip()}
              >
                {t('knowledgeBase.downloadAllVersionsZip')}
              </Button>
              <Popconfirm
                title={t('knowledgeBase.confirmDeleteDocument')}
                onConfirm={() => activeDocId && deleteDocument(activeDocId)}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('knowledgeBase.deleteDocument')}
                </Button>
              </Popconfirm>
            </Space>
            <Alert
              type="info"
              showIcon
              message={xaiEnabled
                ? t('knowledgeBase.ragXaiHint')
                : t('knowledgeBase.ragWorkflowHint')}
              style={{ marginBottom: 4 }}
            />
            <div>
              <Text strong>{t('knowledgeBase.metaPublished')}</Text>{' '}
              {(() => {
                const pv = (detail.versions || []).find((v) => v.id === detail.document.publishedVersionId);
                return pv ? (
                  <Tag color="purple">v{pv.versionNo}</Tag>
                ) : (
                  <Text type="secondary">{t('knowledgeBase.none')}</Text>
                );
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text strong>{t('knowledgeBase.categoryLabel') || '分類'}</Text>
              <Select
                allowClear
                placeholder={t('knowledgeBase.noCategorySet') || '未設定'}
                style={{ minWidth: 180 }}
                loading={movingCategory}
                value={detail.document.categoryId ?? undefined}
                onChange={(val) => moveDocumentCategory(detail.document.id, val ?? null)}
                options={(categories || []).map((c) => ({ value: c.id, label: c.name }))}
              />
              {xaiEnabled && detail.document.categoryId && (() => {
                const cat = (categories || []).find((c) => c.id === detail.document.categoryId);
                return cat?.xaiCollectionId
                  ? <Tag color="blue" style={{ fontSize: 11 }}>xAI ✓</Tag>
                  : <Tag color="default" style={{ fontSize: 11 }}>xAI 未建立</Tag>;
              })()}
            </div>
            <Table
              size="small"
              rowKey="id"
              dataSource={detail.versions || []}
              columns={versionColumns}
              pagination={false}
              scroll={{ x: 780 }}
              rowClassName={(row) => {
                const vid = row.id ?? row.Id;
                return vid === detail.document?.publishedVersionId ? 'kb-published-row' : '';
              }}
            />
          </Space>
        ) : null}
      </Drawer>

      <Modal
        title={t('knowledgeBase.newCategory')}
        open={catModalOpen}
        onCancel={() => setCatModalOpen(false)}
        onOk={createCategory}
        okText={t('common.ok')}
        cancelText={t('common.cancel')}
      >
        <Form form={catForm} layout="vertical">
          <Form.Item name="name" label={t('knowledgeBase.categoryName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label={t('knowledgeBase.parentCategory')}>
            <Select
              allowClear
              options={(categories || []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

